import { useState, useEffect } from "react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  adminLoginStatsApi,
  AdminLoginStatsResponseDto,
} from "@/apis/admin-login-stats";

export interface AdminLoginStat {
  admId: number;
  admNm: string;
  admType: "OWNER" | "MANAGER" | "COACH";
  email: string;
  lastLoginDttm: Date | null;
  loginCount: number;
  totalSessionTime: number; // 분 단위
  avgSessionTime: number; // 분 단위
  isActive: boolean;
}

export interface AdminLoginSummary {
  totalAdmins: number;
  activeAdmins: number;
  totalLogins: number;
  todayLogins: number;
  weeklyLogins: number;
  avgSessionTime: number;
  recentActiveAdmins: number;
}

interface UseAdminLoginStatsProps {
  comId: string;
}

/**
 * 관리자 로그인 통계 데이터를 관리하는 커스텀 훅
 * @description 도장별 관리자들의 로그인 통계를 조회하고 관리합니다.
 * @param comId 도장 ID
 * @returns 통계 데이터, 로딩 상태, 에러 상태 및 유틸 함수들
 */
export default function useAdminLoginStats({ comId }: UseAdminLoginStatsProps) {
  const [stats, setStats] = useState<AdminLoginStat[]>([]);
  const [summaryStats, setSummaryStats] =
    useState<AdminLoginSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 관리자 로그인 통계 데이터를 조회합니다.
   */
  const fetchAdminLoginStats = async () => {
    if (!comId) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log("[ADMIN_LOGIN_STATS_API]", { action: "fetchStats", comId });

      // 실제 API 호출
      const response: AdminLoginStatsResponseDto =
        await adminLoginStatsApi.getStats(comId);

      console.log("[ADMIN_LOGIN_STATS_RAW_DATA]", { response });

      // API 응답 데이터를 컴포넌트에서 사용하는 형태로 변환
      const transformedStats: AdminLoginStat[] = response.admins.map(
        (admin) => {
          // admType을 안전하게 변환 (백엔드에서 올 수 있는 모든 타입 처리)
          let mappedAdmType: "OWNER" | "MANAGER" | "COACH";
          const upperType = admin.admType?.toUpperCase();

          switch (upperType) {
            case "OWNER":
              mappedAdmType = "OWNER";
              break;
            case "MANAGER":
              mappedAdmType = "MANAGER";
              break;
            case "INSTRUCTOR":
            case "COACH":
              mappedAdmType = "COACH";
              break;
            default:
              console.warn("[TYPE_MAPPING_WARNING]", {
                originalType: admin.admType,
                upperType,
                defaultingTo: "COACH",
              });
              mappedAdmType = "COACH";
          }

          return {
            admId: admin.admId,
            admNm: admin.admNm,
            admType: mappedAdmType,
            email: admin.email,
            lastLoginDttm: admin.lastLoginDttm,
            loginCount: admin.loginCount,
            totalSessionTime: admin.totalSessionTime,
            avgSessionTime: admin.avgSessionTime,
            isActive: admin.isActive,
          };
        }
      );

      setSummaryStats({
        totalAdmins: response.summary.totalAdmins,
        activeAdmins: response.summary.activeAdmins,
        totalLogins: response.summary.totalLogins,
        todayLogins: response.summary.totalLogins, // 임시로 totalLogins 사용
        weeklyLogins: Math.round(response.summary.totalLogins * 0.7), // 임시로 totalLogins의 70% 사용
        avgSessionTime: response.summary.avgSessionTime,
        recentActiveAdmins: response.summary.recentActiveAdmins,
      });

      setStats(transformedStats);

      console.log("[ADMIN_LOGIN_STATS_TRANSFORMED]", {
        statsCount: transformedStats.length,
        summaryActive: response.summary.activeAdmins,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error("[ADMIN_LOGIN_STATS_ERROR]", {
        action: "fetchStats",
        comId,
        error: errorMessage,
        fullError: error,
      });
      setError(errorMessage || "관리자 로그인 통계 조회에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 통계 데이터를 새로고침합니다.
   */
  const refreshStats = () => {
    fetchAdminLoginStats();
  };

  /**
   * 세션 시간을 시간:분 형태로 포맷합니다.
   * @param minutes 분 단위 시간
   * @returns 포맷된 시간 문자열
   */
  const formatSessionTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  };

  /**
   * 마지막 로그인 시간을 포맷합니다.
   * @param dateTime 날짜 객체 또는 문자열
   * @returns 포맷된 날짜 문자열
   */
  const formatLastLogin = (dateTime: Date | string | null): string => {
    if (!dateTime) return "로그인 기록 없음";
    try {
      const date = typeof dateTime === "string" ? new Date(dateTime) : dateTime;
      return format(date, "yyyy.MM.dd HH:mm", { locale: ko });
    } catch {
      return "날짜 오류";
    }
  };

  /**
   * 관리자 타입별 정보를 반환합니다.
   * @param type 관리자 타입
   * @returns 타입별 라벨과 색상 정보
   */
  const getAdminTypeInfo = (type: string) => {
    switch (type?.toUpperCase()) {
      case "OWNER":
        return { label: "오너", colorType: "error" as const };
      case "MANAGER":
        return { label: "매니저", colorType: "primary" as const };
      case "COACH":
      case "INSTRUCTOR":
        return { label: "코치", colorType: "success" as const };
      default:
        return { label: "기타", colorType: "default" as const };
    }
  };

  /**
   * 관리자 타입별 통계를 계산합니다.
   */
  const getTypeStats = () => {
    const typeStats = {
      OWNER: stats.filter((admin) => admin.admType === "OWNER").length,
      MANAGER: stats.filter((admin) => admin.admType === "MANAGER").length,
      COACH: stats.filter((admin) => admin.admType === "COACH").length,
    };

    return typeStats;
  };

  // 컴포넌트 마운트 시 데이터 조회
  useEffect(() => {
    fetchAdminLoginStats();
  }, [comId]);

  return {
    // 데이터
    stats,
    summaryStats,
    typeStats: getTypeStats(),

    // 상태
    isLoading,
    error,

    // 유틸 함수
    formatSessionTime,
    formatLastLogin,
    getAdminTypeInfo,

    // 액션 함수
    refreshStats,
    fetchAdminLoginStats,
  };
}
